import React, { memo, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import SignupForm from "./SignupForm";
import SignupDisallowed from "./SignupDisallowed";
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import "./signup.css";
import pagePath from "../../util/constants/pagePath";

const certs = [
  {
    code: "CCSP",
    tone: "ccsp",
    fullName: "Certified Cloud Security Professional",
  },
  {
    code: "CISM",
    tone: "cism",
    fullName: "Certified Information Security Manager",
  },
  {
    code: "SSCP",
    tone: "sscp",
    fullName: "Systems Security Certified Practitioner",
  },
  {
    code: "CISA",
    tone: "cisa",
    fullName: "Certified Information Systems Auditor",
  },
  {
    code: "CISSP",
    tone: "cissp",
    selectedValue: "cissp-2024",
    fullName: "Certified Information Systems Security Professional",
  },
  {
    code: "CRISC",
    tone: "crisc",
    fullName: "Certified in Risk and Information Systems Control",
  },
];

const CertificateButton = memo(function CertificateButton({
  cert,
  isActive,
  onPreview,
  onSelect,
}) {
  return (
    <button
      type="button"
      className={`certOval certOval-${cert.tone} ${isActive ? "active" : ""}`}
      onMouseEnter={() => onPreview(cert.selectedValue || cert.code)}
      onFocus={() => onPreview(cert.selectedValue || cert.code)}
      onClick={() => onSelect(cert.selectedValue || cert.code)}
    >
      <span className="certOvalCode">{cert.code}</span>
    </button>
  );
});

const Signup = () => {
  const { platform, os } = useSelector((state) => state.device_context);
  const [selectedCert, setSelectedCert] = useState(null);
  const [activeCertPreview, setActiveCertPreview] = useState(null);
  const [planError, setPlanError] = useState("");
  const errorRef = useRef(null);
  const emailRef = useRef(null);
  const certRowRef = useRef(null);

  const isSignupAllowed = useMemo(() => {
    if (platform === "mobile_app" && os === "ios") return false;
    return true;
  }, [platform, os]);

  const showPlanError = () => {
    setPlanError("Choose your certification to sign up");
    setTimeout(() => {
      errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);
    setTimeout(() => setPlanError(""), 2000);
  };

  const selectedCertMeta = certs.find(
    (cert) => (cert.selectedValue || cert.code) === selectedCert
  );

  const certDisplayMeta = selectedCertMeta;

  const handleCertSelect = (certCode) => {
    setPlanError("");
    setActiveCertPreview(certCode);
    setSelectedCert((current) => {
      const next = current === certCode ? null : certCode;

      if (next) {
        localStorage.setItem("selected_cert_code", next.toLowerCase());
      } else {
        localStorage.removeItem("selected_cert_code");
      }

      return next;
    });
  };

  const handleCertRowBlur = (event) => {
    if (certRowRef.current?.contains(event.relatedTarget)) return;
    setActiveCertPreview(selectedCert);
  };

  return (
    <div className="signup">
      <div className="signupHero signupHero-exact">
        <div className="certWrap">
          <div className="heroCopy">
            <h1 className="certTitle">
              Master your certification exam<br />anytime, anywhere.
            </h1>
            <p className="certTagline">
              Full access for just $30, every 2 months.
            </p>
            <h2 className="certTitle hero-signup-heading">Sign Up</h2>
          </div>
        </div>
      </div>
      <div
        ref={certRowRef}
        className="certRowFrame"
        onMouseLeave={() => setActiveCertPreview(selectedCert)}
        onBlurCapture={handleCertRowBlur}
      >
        <div className="certSelectionIntro">
          <p className="certSelectionKicker">CHOOSE YOUR TRACK</p>
          <h3>Select Certification To Get Started</h3>
          <p>Your practice access will be tailored to the certification you select here.</p>
        </div>
        <div className={`certGrid certGridOval ${selectedCert ? "has-selection" : ""}`}>
          {certs.map((cert) => (
            <CertificateButton
              key={cert.code}
              cert={cert}
              isActive={selectedCert === (cert.selectedValue || cert.code)}
              onPreview={setActiveCertPreview}
              onSelect={handleCertSelect}
            />
          ))}
        </div>
        <div className={`selection-cart-compact ${selectedCert ? "has-selection" : "is-empty"}`}>
          <div className="cart-main-content">
            <ShoppingCartIcon className="cart-icon-small" />
            {selectedCert ? (
              <>
                <span className="cart-item-info">
                  <strong>{certDisplayMeta?.code}</strong>
                  <span className="cart-item-sep">•</span>
                  {certDisplayMeta?.fullName}
                </span>
                <span className="cart-item-price">$30</span>
              </>
            ) : (
              <span className="cart-item-info placeholder">
                Your cart is empty – Select an exam above
              </span>
            )}
          </div>
          {selectedCert && (
            <button
              type="button"
              className="cart-close-btn"
              onClick={() => handleCertSelect(selectedCert)}
              aria-label="Remove selection"
            >
              <CloseIcon fontSize="inherit" />
            </button>
          )}
        </div>
      </div>

      {planError ? (
        <div className="signup-form-shell">
          <div ref={errorRef} className="betweenError">
            {planError}
          </div>
        </div>
      ) : null}

      {isSignupAllowed === true ? (
        <SignupForm
          selectedPlan={selectedCert}
          onPlanRequired={showPlanError}
          emailRef={emailRef}
        />
      ) : (
        <SignupDisallowed
          onCancel={() => (window.location.href = pagePath.login)}
          continuePath={pagePath.ma_redirect_signup}
        />
      )}
    </div>
  );
};

export default Signup;